import type { CustomerFinance } from "types/Customer";
import type { FileEntity } from "types/Files";

/**
 * Where a customer's master agreement stands on the way to being in force.
 *
 * Signing it by hand is a deliberate two-step flow: the client signs a paper
 * copy and someone uploads it (`awaiting_client` -> `awaiting_company`), then
 * our company signs that uploaded copy in Dokobit. Only the Dokobit
 * completion marks the file signed. People kept missing the second step, so
 * the finance and order pages use this stage to say what is missing.
 */
export type MasterAgreementStage =
  | "no_finance"
  | "not_approved"
  | "no_document"
  | "withdrawn"
  | "awaiting_client"
  | "awaiting_company"
  | "awaiting_signatures"
  | "signed";

// `customer.finance` is typed `CustomerFinance | string` and a legacy
// `masterAgreementFileId` can be a bare id, so anything that is not an object
// means "nothing loaded here".
function isLoaded<T extends object>(value: T | string | null | undefined): value is T {
  return typeof value === "object" && value !== null;
}

// The file part of the stage, for callers that cannot read the finance record
// (a bare finance id) but do have the agreement file. The order of the checks
// matters: an annulled document that is still flagged signed is withdrawn.
export function getAgreementFileStage(
  file: FileEntity | string | null | undefined
): MasterAgreementStage {
  if (!isLoaded(file)) return "no_document";
  if (file.annulledAt || file.withdrawnAt) return "withdrawn";
  if (file.isSigned === true) return "signed";
  if (file.signType === "Manual") return "awaiting_client";
  if (file.signType === "Dokobit" && file.signingStepsRequired === 2) {
    return "awaiting_company";
  }
  return "awaiting_signatures";
}

// A draft stays "not approved" even when the approval flag is set.
export function getMasterAgreementStage(
  finance: CustomerFinance | string | null | undefined,
  file: FileEntity | string | null | undefined
): MasterAgreementStage {
  if (!isLoaded(finance)) return "no_finance";
  if (finance.status === "draft" || finance.isApproved !== true) return "not_approved";
  return getAgreementFileStage(file);
}
